import {
  normalizeProjectSnapshot,
  PROJECT_SNAPSHOT_VERSION,
  type ProjectSnapshot,
} from '@/project/projectSnapshot'

export const EDITOR_PROJECT_DRAFT_STORAGE_KEY = 'imockup-editor-project-draft-v1'

const IDB_NAME = 'imockup-editor-draft'
const IDB_VERSION = 2
const IDB_STORE = 'kv'
const IDB_ASSET_STORE = 'draftAssets'
const DRAFT_ASSET_MANIFEST_KEY = `${EDITOR_PROJECT_DRAFT_STORAGE_KEY}:assets`

const DRAFT_ENVELOPE_VERSION = 1 as const
const LOCALSTORAGE_SOFT_LIMIT_CHARS = 4_000_000
const DEFAULT_DEBOUNCE_MS = 2000
const DEFAULT_MAX_INTERVAL_MS = 30_000
const MAX_INTERVAL_TICK_MS = 4000

export type EditorProjectDraftEnvelope = {
  envelopeVersion: typeof DRAFT_ENVELOPE_VERSION
  savedAt: number
  snapshot: ProjectSnapshot
}

export type EditorDraftAssetInput = {
  assetKey: string
  objectUrl: string
  fileName?: string | null
}

export type EditorDraftAssetRecord = {
  assetKey: string
  blob: Blob
  fileName: string | null
  mimeType: string | null
}

export type EditorProjectDraftReadResult = {
  snapshot: ProjectSnapshot
  savedAt: number
  assets: Map<string, EditorDraftAssetRecord>
}

type StoredDraftAssetRecord = {
  blob: Blob
  fileName: string | null
  mimeType: string | null
}

type PersistEditorDraftAssetsOptions = {
  assetKeys: string[]
  upserts: EditorDraftAssetInput[]
  removedAssetKeys: string[]
}

export type EditorDraftAutoSaveControllerOptions = {
  getSnapshot: () => ProjectSnapshot
  getDraftAssets?: () => EditorDraftAssetInput[] | Promise<EditorDraftAssetInput[]>
  shouldSave: () => boolean
  debounceMs?: number
  maxIntervalMs?: number
  maxConsecutiveFailures?: number
  onPersistFailure?: (consecutiveFailures: number) => void
}

export function editorDraftBackgroundAssetKey(): string {
  return 'background-image'
}

export function editorDraftLayerVideoAssetKey(layerId: string): string {
  return `layer:${layerId}:video`
}

export function editorDraftLayerScreenImageAssetKey(layerId: string, imageId: string): string {
  return `layer:${layerId}:image:${imageId}`
}

export function editorDraftLayerLegacyImageAssetKey(layerId: string): string {
  return `layer:${layerId}:legacy-image`
}

function isQuotaDomException(e: unknown): boolean {
  return (
    typeof DOMException !== 'undefined' &&
    e instanceof DOMException &&
    (e.name === 'QuotaExceededError' || e.code === 22 || e.code === 1014)
  )
}

function openIdb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('indexedDB unavailable'))
      return
    }
    const req = indexedDB.open(IDB_NAME, IDB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE)
      }
      if (!db.objectStoreNames.contains(IDB_ASSET_STORE)) {
        db.createObjectStore(IDB_ASSET_STORE)
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'))
  })
}

async function idbPutRaw(key: string, value: string): Promise<void> {
  const db = await openIdb()
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite')
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error ?? new Error('idb tx'))
      tx.onabort = () => reject(tx.error ?? new Error('idb tx abort'))
      tx.objectStore(IDB_STORE).put(value, key)
    })
  } finally {
    db.close()
  }
}

async function idbGetRaw(key: string): Promise<string | undefined> {
  const db = await openIdb()
  try {
    return await new Promise<string | undefined>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readonly')
      tx.onerror = () => reject(tx.error ?? new Error('idb read tx'))
      const req = tx.objectStore(IDB_STORE).get(key)
      req.onsuccess = () => resolve(typeof req.result === 'string' ? req.result : undefined)
      req.onerror = () => reject(req.error ?? new Error('idb get'))
    })
  } finally {
    db.close()
  }
}

async function idbDeleteRaw(key: string): Promise<void> {
  try {
    const db = await openIdb()
    try {
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, 'readwrite')
        tx.oncomplete = () => resolve()
        tx.onerror = () => reject(tx.error ?? new Error('idb del tx'))
        tx.onabort = () => reject(tx.error ?? new Error('idb del abort'))
        tx.objectStore(IDB_STORE).delete(key)
      })
    } finally {
      db.close()
    }
  } catch {
    /* ignore */
  }
}

async function idbGetAssetManifest(): Promise<string[]> {
  if (typeof indexedDB === 'undefined') return []
  const raw = await idbGetRaw(DRAFT_ASSET_MANIFEST_KEY).catch(() => undefined)
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.filter((x): x is string => typeof x === 'string' && x.length > 0)
  } catch {
    return []
  }
}

async function idbPutAssetManifest(assetKeys: readonly string[]): Promise<void> {
  if (typeof indexedDB === 'undefined') return
  await idbPutRaw(DRAFT_ASSET_MANIFEST_KEY, JSON.stringify(Array.from(new Set(assetKeys))))
}

async function idbDeleteAssetManifest(): Promise<void> {
  if (typeof indexedDB === 'undefined') return
  await idbDeleteRaw(DRAFT_ASSET_MANIFEST_KEY)
}

async function idbPutAssets(records: readonly EditorDraftAssetRecord[]): Promise<void> {
  if (records.length === 0) return
  const db = await openIdb()
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_ASSET_STORE, 'readwrite')
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error ?? new Error('idb asset put tx'))
      tx.onabort = () => reject(tx.error ?? new Error('idb asset put abort'))
      const store = tx.objectStore(IDB_ASSET_STORE)
      for (const record of records) {
        const value: StoredDraftAssetRecord = {
          blob: record.blob,
          fileName: record.fileName,
          mimeType: record.mimeType,
        }
        store.put(value, record.assetKey)
      }
    })
  } finally {
    db.close()
  }
}

async function idbDeleteAssets(assetKeys: readonly string[]): Promise<void> {
  if (assetKeys.length === 0) return
  if (typeof indexedDB === 'undefined') return
  const db = await openIdb()
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_ASSET_STORE, 'readwrite')
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error ?? new Error('idb asset delete tx'))
      tx.onabort = () => reject(tx.error ?? new Error('idb asset delete abort'))
      const store = tx.objectStore(IDB_ASSET_STORE)
      for (const assetKey of assetKeys) {
        store.delete(assetKey)
      }
    })
  } finally {
    db.close()
  }
}

async function idbReadAssets(assetKeys: readonly string[]): Promise<Map<string, EditorDraftAssetRecord>> {
  const out = new Map<string, EditorDraftAssetRecord>()
  if (assetKeys.length === 0 || typeof indexedDB === 'undefined') return out
  const db = await openIdb()
  try {
    const reads = assetKeys.map(
      (assetKey) =>
        new Promise<void>((resolve, reject) => {
          const tx = db.transaction(IDB_ASSET_STORE, 'readonly')
          tx.onerror = () => reject(tx.error ?? new Error('idb asset read tx'))
          const req = tx.objectStore(IDB_ASSET_STORE).get(assetKey)
          req.onsuccess = () => {
            const result = req.result as StoredDraftAssetRecord | undefined
            if (result?.blob instanceof Blob) {
              out.set(assetKey, {
                assetKey,
                blob: result.blob,
                fileName: result.fileName ?? null,
                mimeType: result.mimeType ?? result.blob.type ?? null,
              })
            }
            resolve()
          }
          req.onerror = () => reject(req.error ?? new Error('idb asset get'))
        }),
    )
    await Promise.all(reads)
  } finally {
    db.close()
  }
  return out
}

async function blobFromObjectUrl(objectUrl: string): Promise<Blob> {
  const res = await fetch(objectUrl)
  if (!res.ok) {
    throw new Error(`draft asset fetch failed: ${res.status}`)
  }
  return await res.blob()
}

async function persistEditorDraftAssets(opts: PersistEditorDraftAssetsOptions | undefined): Promise<void> {
  if (typeof indexedDB === 'undefined') {
    if ((opts?.assetKeys.length ?? 0) > 0) {
      throw new Error('indexedDB unavailable')
    }
    return
  }

  const existing = await idbGetAssetManifest()
  if (!opts) {
    await idbDeleteAssets(existing)
    await idbDeleteAssetManifest()
    return
  }

  const nextKeys = Array.from(new Set(opts.assetKeys))
  const staleKeys = existing.filter((key) => !nextKeys.includes(key))
  const removedKeys = Array.from(new Set([...staleKeys, ...opts.removedAssetKeys]))
  if (removedKeys.length > 0) {
    await idbDeleteAssets(removedKeys)
  }

  if (opts.upserts.length > 0) {
    const records = await Promise.all(
      opts.upserts.map(async (asset): Promise<EditorDraftAssetRecord> => {
        const blob = await blobFromObjectUrl(asset.objectUrl)
        return {
          assetKey: asset.assetKey,
          blob,
          fileName: asset.fileName ?? null,
          mimeType: blob.type || null,
        }
      }),
    )
    await idbPutAssets(records)
  }

  if (nextKeys.length > 0) {
    await idbPutAssetManifest(nextKeys)
  } else {
    await idbDeleteAssetManifest()
  }
}

function tryParseEnvelopeJson(raw: string | null | undefined): EditorProjectDraftEnvelope | null {
  if (raw == null || raw === '') return null
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object') return null
    const obj = parsed as Record<string, unknown>
    if (obj.envelopeVersion !== DRAFT_ENVELOPE_VERSION) return null
    const savedAt = Number(obj.savedAt)
    if (!Number.isFinite(savedAt)) return null
    const normalized = normalizeProjectSnapshot(obj.snapshot)
    if (!normalized || normalized.version !== PROJECT_SNAPSHOT_VERSION) return null
    return {
      envelopeVersion: DRAFT_ENVELOPE_VERSION,
      savedAt,
      snapshot: normalized,
    }
  } catch {
    return null
  }
}

export function clearEditorDraft(): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(EDITOR_PROJECT_DRAFT_STORAGE_KEY)
    }
  } catch {
    /* ignore */
  }
  void idbDeleteRaw(EDITOR_PROJECT_DRAFT_STORAGE_KEY)
  void (async () => {
    const assetKeys = await idbGetAssetManifest().catch(() => [])
    await idbDeleteAssets(assetKeys).catch(() => undefined)
    await idbDeleteAssetManifest().catch(() => undefined)
  })()
}

export async function readEditorDraftAsync(): Promise<EditorProjectDraftReadResult | null> {
  let rawLs: string | null
  try {
    rawLs = typeof localStorage !== 'undefined' ? localStorage.getItem(EDITOR_PROJECT_DRAFT_STORAGE_KEY) : null
  } catch {
    rawLs = null
  }

  let env = tryParseEnvelopeJson(rawLs)
  if (env) {
    const assetKeys = await idbGetAssetManifest().catch(() => [])
    const assets = await idbReadAssets(assetKeys).catch(() => new Map<string, EditorDraftAssetRecord>())
    return { snapshot: env.snapshot, savedAt: env.savedAt, assets }
  }

  if (rawLs != null && rawLs !== '') {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(EDITOR_PROJECT_DRAFT_STORAGE_KEY)
      }
    } catch {
      /* ignore */
    }
  }

  const rawIdb = await idbGetRaw(EDITOR_PROJECT_DRAFT_STORAGE_KEY).catch(() => undefined)
  env = tryParseEnvelopeJson(rawIdb)
  if (env) {
    const assetKeys = await idbGetAssetManifest().catch(() => [])
    const assets = await idbReadAssets(assetKeys).catch(() => new Map<string, EditorDraftAssetRecord>())
    return { snapshot: env.snapshot, savedAt: env.savedAt, assets }
  }

  if (rawIdb != null && rawIdb !== '') {
    void idbDeleteRaw(EDITOR_PROJECT_DRAFT_STORAGE_KEY)
  }
  return null
}

export async function persistEditorDraft(
  snapshot: ProjectSnapshot,
  assets?: PersistEditorDraftAssetsOptions,
): Promise<boolean> {
  const envelope: EditorProjectDraftEnvelope = {
    envelopeVersion: DRAFT_ENVELOPE_VERSION,
    savedAt: Date.now(),
    snapshot,
  }

  let json: string
  try {
    json = JSON.stringify(envelope)
  } catch {
    return false
  }

  if (typeof localStorage !== 'undefined') {
    try {
      if (json.length <= LOCALSTORAGE_SOFT_LIMIT_CHARS) {
        localStorage.setItem(EDITOR_PROJECT_DRAFT_STORAGE_KEY, json)
        void idbDeleteRaw(EDITOR_PROJECT_DRAFT_STORAGE_KEY)
        try {
          await persistEditorDraftAssets(assets)
          return true
        } catch {
          return false
        }
      }
    } catch (e) {
      if (!isQuotaDomException(e)) {
        return false
      }
    }
  }

  try {
    await idbPutRaw(EDITOR_PROJECT_DRAFT_STORAGE_KEY, json)
    await persistEditorDraftAssets(assets)
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(EDITOR_PROJECT_DRAFT_STORAGE_KEY)
      }
    } catch {
      /* ignore */
    }
    return true
  } catch {
    return false
  }
}

export function createEditorDraftAutoSaveController(
  opts: EditorDraftAutoSaveControllerOptions,
): {
  markDirty: () => void
  dispose: () => void
  flushSyncBestEffort: () => void
} {
  const debounceMs = opts.debounceMs ?? DEFAULT_DEBOUNCE_MS
  const maxIntervalMs = opts.maxIntervalMs ?? DEFAULT_MAX_INTERVAL_MS
  const maxConsecutiveFailures = opts.maxConsecutiveFailures ?? 3

  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  let maxIntervalTimer: ReturnType<typeof setInterval> | null = null
  let hasPendingChanges = false
  let lastSuccessfulFlushAt = Date.now()
  let consecutiveFailures = 0
  let paused = false
  let flushing = false
  let lastDraftAssets = new Map<string, { objectUrl: string; fileName: string | null }>()

  function clearDebounce(): void {
    if (debounceTimer != null) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
  }

  function stopMaxInterval(): void {
    if (maxIntervalTimer != null) {
      clearInterval(maxIntervalTimer)
      maxIntervalTimer = null
    }
  }

  async function flush(): Promise<void> {
    if (flushing || paused || !opts.shouldSave() || !hasPendingChanges) return
    flushing = true
    const snap = opts.getSnapshot()
    try {
      const draftAssets = opts.getDraftAssets ? await Promise.resolve(opts.getDraftAssets()) : []
      const nextDraftAssets = new Map<string, { objectUrl: string; fileName: string | null }>()
      for (const asset of draftAssets) {
        nextDraftAssets.set(asset.assetKey, {
          objectUrl: asset.objectUrl,
          fileName: asset.fileName ?? null,
        })
      }
      const upserts = draftAssets.filter((asset) => {
        const prev = lastDraftAssets.get(asset.assetKey)
        return prev?.objectUrl !== asset.objectUrl || prev?.fileName !== (asset.fileName ?? null)
      })
      const removedAssetKeys = Array.from(lastDraftAssets.keys()).filter((key) => !nextDraftAssets.has(key))
      const ok = await persistEditorDraft(snap, {
        assetKeys: Array.from(nextDraftAssets.keys()),
        upserts,
        removedAssetKeys,
      })
      if (ok) {
        hasPendingChanges = false
        consecutiveFailures = 0
        lastSuccessfulFlushAt = Date.now()
        lastDraftAssets = nextDraftAssets
      } else {
        consecutiveFailures += 1
        opts.onPersistFailure?.(consecutiveFailures)
        if (consecutiveFailures >= maxConsecutiveFailures) {
          paused = true
          stopMaxInterval()
          clearDebounce()
        }
      }
    } finally {
      flushing = false
    }
  }

  function scheduleDebouncedFlush(): void {
    clearDebounce()
    debounceTimer = setTimeout(() => {
      debounceTimer = null
      void flush()
    }, debounceMs)
  }

  function markDirty(): void {
    if (paused || !opts.shouldSave()) return
    hasPendingChanges = true

    const sinceSuccess = Date.now() - lastSuccessfulFlushAt
    if (lastSuccessfulFlushAt > 0 && sinceSuccess >= maxIntervalMs) {
      void flush()
      return
    }

    scheduleDebouncedFlush()

    if (maxIntervalTimer == null) {
      maxIntervalTimer = setInterval(() => {
        if (paused || !opts.shouldSave() || !hasPendingChanges) return
        if (Date.now() - lastSuccessfulFlushAt >= maxIntervalMs) {
          void flush()
        }
      }, MAX_INTERVAL_TICK_MS)
    }
  }

  function flushSyncBestEffort(): void {
    if (paused || !opts.shouldSave()) return
    clearDebounce()
    stopMaxInterval()
    try {
      const envelope: EditorProjectDraftEnvelope = {
        envelopeVersion: DRAFT_ENVELOPE_VERSION,
        savedAt: Date.now(),
        snapshot: opts.getSnapshot(),
      }
      const json = JSON.stringify(envelope)
      if (typeof localStorage !== 'undefined' && json.length <= LOCALSTORAGE_SOFT_LIMIT_CHARS) {
        localStorage.setItem(EDITOR_PROJECT_DRAFT_STORAGE_KEY, json)
        hasPendingChanges = false
        lastSuccessfulFlushAt = Date.now()
      }
    } catch {
      /* ignore */
    }
  }

  function dispose(): void {
    clearDebounce()
    stopMaxInterval()
    flushSyncBestEffort()
  }

  return { markDirty, dispose, flushSyncBestEffort }
}
